import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL, API_BASE_URL } from '../config';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// Approval timestamps come back as naive UTC — append 'Z' so they render in the
// viewer's local time instead of being read as local.
const formatStamp = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr.endsWith('Z') ? isoStr : isoStr + 'Z');
  if (isNaN(d)) return '';
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  });
};

function PublicQuoteApproval() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [action, setAction] = useState(null); // 'approved' | 'declined'
  const [submitting, setSubmitting] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  // Per-item selection: set of line-item IDs the client wants. Starts with all selected.
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());
  const [schedulingNotes, setSchedulingNotes] = useState('');
  // Typed e-signature — required to approve, so the approval carries a name we can stand on
  const [signature, setSignature] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/public/quote/${token}/data`)
      .then(res => {
        if (!res.ok) throw new Error('Quote not found');
        return res.json();
      })
      .then(data => {
        setQuote(data);
        if (data.status === 'Approved' || data.status === 'Partially Approved') setAction('approved');
        else if (data.status === 'Declined') setAction('declined');
        // Start with every line item selected
        setSelectedItemIds(new Set((data.line_items || []).map(li => li.id)));
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [token]);

  const toggleItem = (item) => {
    if (item.is_required) return; // can't deselect required items
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  };

  // Live totals based on current selection
  const selectedLineItems = (quote?.line_items || []).filter(li => selectedItemIds.has(li.id));
  const liveSubtotal = selectedLineItems.reduce((sum, li) => sum + (li.amount || 0), 0);
  const liveDiscount = quote?.discount_percent ? liveSubtotal * (quote.discount_percent / 100) : 0;
  const liveTotal = liveSubtotal - liveDiscount;
  const anyDeclined = quote && (quote.line_items || []).length > 0 && selectedLineItems.length < quote.line_items.length;

  const handleApprove = async () => {
    if (selectedLineItems.length === 0) {
      alert('Please select at least one item to approve, or use Decline if you don\'t want any of the work.');
      return;
    }
    if (!signature.trim()) {
      alert('Please type your full name to sign and approve this quote.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/public/quote/${token}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved_line_item_ids: Array.from(selectedItemIds),
          scheduling_notes: schedulingNotes,
          signature: signature.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setAction('approved');
        setQuote(prev => ({
          ...prev,
          status: data.status || 'Approved',
          approved_at: data.approved_at,
          approved_by_name: data.approved_by_name,
        }));
      } else {
        alert(data.error || 'Failed to approve');
      }
    } catch {
      alert('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  const handleDecline = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/public/quote/${token}/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: declineReason })
      });
      const data = await res.json();
      if (res.ok) {
        setAction('declined');
        setQuote(prev => ({ ...prev, status: 'Declined' }));
      } else {
        alert(data.error || 'Failed to decline');
      }
    } catch {
      alert('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f3f4f6' }}>
        <div style={{ textAlign: 'center', color: '#6b7280' }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>Loading quote...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f3f4f6' }}>
        <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>404</div>
          <h2 style={{ margin: '0 0 8px', color: '#1f2937' }}>Quote Not Found</h2>
          <p style={{ color: '#6b7280', margin: 0 }}>This quote link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  // Mirror the server rule (it only accepts approve/decline on Sent or Draft) rather
  // than listing the inverse — otherwise every new status silently re-opens the buttons.
  // Missing 'Partially Approved' here previously left the approve panel up after a
  // partial approval, so a second attempt failed with a confusing error.
  const alreadyActioned = !['Draft', 'Sent'].includes(quote.status);

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '20px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Company Header */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px 32px',
          marginBottom: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            {quote.company_logo && (
              <img src={`${API_URL.replace('/api', '')}/uploads/logos/${quote.company_logo.split('/').pop()}`} alt="Logo" style={{ maxHeight: '60px', marginBottom: '8px' }} />
            )}
            <h1 style={{ margin: '0 0 4px', fontSize: '24px', color: '#1f2937' }}>{quote.company_name}</h1>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
              {quote.company_address && <>{quote.company_address}<br /></>}
              {quote.company_city_state_zip && <>{quote.company_city_state_zip}<br /></>}
              {quote.company_phone && <>{quote.company_phone}<br /></>}
              {quote.company_email}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <h2 style={{ margin: '0 0 4px', fontSize: '28px', color: '#16a34a' }}>QUOTE</h2>
            <p style={{ margin: '0 0 8px', fontSize: '16px', color: '#6b7280' }}>{quote.quote_number}</p>
            <span style={{
              display: 'inline-block',
              padding: '4px 12px',
              background: (quote.status === 'Approved' || quote.status === 'Partially Approved') ? '#16a34a' : quote.status === 'Declined' ? '#dc2626' : quote.status === 'Sent' ? '#2563eb' : '#6b7280',
              color: 'white',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {quote.status}
            </span>
          </div>
        </div>

        {/* Action result banner */}
        {action === 'approved' && (
          <div style={{
            background: '#dcfce7',
            border: '1px solid #86efac',
            borderRadius: '12px',
            padding: '20px 24px',
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>&#10003;</div>
            <h3 style={{ margin: '0 0 8px', color: '#15803d', fontSize: '20px' }}>Quote Approved!</h3>
            <p style={{ margin: 0, color: '#166534' }}>Thank you! We'll be in touch shortly to get started.</p>
            {quote.approved_at && quote.approved_by_name && (
              <p style={{ margin: '12px 0 0', color: '#166534', fontSize: '13px' }}>
                Signed by <strong>{quote.approved_by_name}</strong><br />
                {formatStamp(quote.approved_at)}
              </p>
            )}
          </div>
        )}
        {action === 'declined' && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '20px 24px',
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 8px', color: '#dc2626', fontSize: '20px' }}>Quote Declined</h3>
            <p style={{ margin: 0, color: '#991b1b' }}>We appreciate your consideration. Please contact us if you have any questions.</p>
          </div>
        )}

        {/* Quote details */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px 32px',
          marginBottom: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h3 style={{ margin: '0 0 8px', color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Prepared For</h3>
              <p style={{ margin: 0, fontSize: '15px', lineHeight: '1.6' }}>
                <strong>{quote.bill_to_name}</strong><br />
                {quote.bill_to_company && <>{quote.bill_to_company}<br /></>}
                {quote.bill_to_address && <>{quote.bill_to_address}<br /></>}
                {quote.bill_to_city_state_zip && <>{quote.bill_to_city_state_zip}<br /></>}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Quote Date:</strong> {formatDate(quote.quote_date)}</p>
              <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>Valid Until:</strong> {formatDate(quote.valid_until)}</p>
              {quote.po_number && <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>PO #:</strong> {quote.po_number}</p>}
            </div>
          </div>

          {/* Item selection hint — only before the client has actioned the quote */}
          {!alreadyActioned && (
            <div style={{
              background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px',
              padding: '10px 14px', marginBottom: '12px', fontSize: '13px', color: '#1e40af',
            }}>
              Check the items you'd like to proceed with. Uncheck anything you don't want — your total updates automatically.
            </div>
          )}

          {/* Line items */}
          {(() => {
            const isMobile = window.innerWidth < 768;
            if (!isMobile) {
              return (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      {!alreadyActioned && <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontSize: '13px', width: '44px' }}>Want</th>}
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontSize: '13px' }}>Description</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontSize: '13px', width: '70px' }}>Qty</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e5e7eb', fontSize: '13px', width: '100px' }}>Rate</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e5e7eb', fontSize: '13px', width: '100px' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(quote.line_items || []).map((item, i) => {
                      const isSelected = selectedItemIds.has(item.id);
                      const declinedNow = alreadyActioned ? item.approval_status === 'declined' : !isSelected;
                      return (
                        <tr key={i} style={{ opacity: declinedNow ? 0.45 : 1 }}>
                          {!alreadyActioned && (
                            <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={item.is_required}
                                onChange={() => toggleItem(item)}
                                style={{ width: '18px', height: '18px', cursor: item.is_required ? 'not-allowed' : 'pointer' }}
                              />
                            </td>
                          )}
                          <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', textDecoration: declinedNow ? 'line-through' : 'none' }}>
                            {item.description}
                            {item.is_required && (
                              <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: '700', color: '#6b7280', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>
                                REQUIRED
                              </span>
                            )}
                            {alreadyActioned && item.approval_status === 'declined' && (
                              <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: '700', color: '#dc2626' }}>DECLINED</span>
                            )}
                          </td>
                          <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>{item.quantity}</td>
                          <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>{formatCurrency(item.rate)}</td>
                          <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>{formatCurrency(item.amount)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            }
            // Mobile: stack each line item as a card so nothing runs off-screen
            return (
              <div style={{ marginBottom: '24px' }}>
                {(quote.line_items || []).map((item, i) => {
                  const isSelected = selectedItemIds.has(item.id);
                  const declinedNow = alreadyActioned ? item.approval_status === 'declined' : !isSelected;
                  return (
                    <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', marginBottom: '10px', opacity: declinedNow ? 0.5 : 1 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        {!alreadyActioned && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={item.is_required}
                            onChange={() => toggleItem(item)}
                            style={{ width: '20px', height: '20px', marginTop: '2px', flexShrink: 0, cursor: item.is_required ? 'not-allowed' : 'pointer' }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', color: '#1f2937', textDecoration: declinedNow ? 'line-through' : 'none' }}>
                            {item.description}
                            {item.is_required && (
                              <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: '700', color: '#6b7280', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>REQUIRED</span>
                            )}
                            {alreadyActioned && item.approval_status === 'declined' && (
                              <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: '700', color: '#dc2626' }}>DECLINED</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '8px', fontSize: '13px', color: '#6b7280', gap: '8px' }}>
                            <span>Qty {item.quantity} &times; {formatCurrency(item.rate)}</span>
                            <span style={{ fontWeight: '700', color: '#1f2937', fontSize: '15px' }}>{formatCurrency(item.amount)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Totals — live values before the client actions the quote, stored values after */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: '280px', maxWidth: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span>Subtotal</span>
                <span>{formatCurrency(alreadyActioned ? quote.subtotal : liveSubtotal)}</span>
              </div>
              {quote.discount_percent > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', color: '#dc2626' }}>
                  <span>Discount ({quote.discount_percent}%)</span>
                  <span>-{formatCurrency(alreadyActioned ? quote.discount_amount : liveDiscount)}</span>
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderTop: '2px solid #333',
                marginTop: '8px',
                fontSize: '20px',
                fontWeight: '700'
              }}>
                <span>Total</span>
                <span>{formatCurrency(alreadyActioned ? quote.total : liveTotal)}</span>
              </div>
              {!alreadyActioned && anyDeclined && (
                <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'right', marginTop: '4px' }}>
                  Total reflects only the items you've selected.
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {quote.notes && (
            <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ margin: '0 0 8px', color: '#6b7280', fontSize: '12px', textTransform: 'uppercase' }}>Notes</h4>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '14px', color: '#374151' }}>{quote.notes}</p>
            </div>
          )}
          {quote.terms && (
            <div style={{ marginTop: '16px' }}>
              <h4 style={{ margin: '0 0 8px', color: '#6b7280', fontSize: '12px', textTransform: 'uppercase' }}>Terms</h4>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>{quote.terms}</p>
            </div>
          )}

          {/* Sketches */}
          {(quote.sketches || []).length > 0 && (
            <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ margin: '0 0 12px', color: '#6b7280', fontSize: '12px', textTransform: 'uppercase' }}>Site Sketches</h4>
              {quote.sketches.map(s => (
                <div key={s.id} style={{ marginBottom: '20px' }}>
                  {s.name && <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>{s.name}</div>}
                  {s.thumbnail_url && (
                    <img
                      src={`${API_BASE_URL}${s.thumbnail_url}`}
                      alt={s.name || 'Sketch'}
                      style={{ width: '100%', maxWidth: '100%', border: '1px solid #e5e7eb', borderRadius: '8px', display: 'block' }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Approve / Decline buttons */}
        {!alreadyActioned && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '20px', color: '#1f2937' }}>Would you like to proceed?</h3>
            <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: '14px' }}>
              Approve the selected items to get started, or decline if you'd like to discuss further.
            </p>

            {/* Scheduling preferences */}
            {!showDeclineForm && (
              <div style={{ marginBottom: '20px', textAlign: 'left', maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Scheduling preferences <span style={{ fontWeight: '400', color: '#9ca3af' }}>(optional)</span>
                </label>
                <textarea
                  value={schedulingNotes}
                  onChange={(e) => setSchedulingNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g., Please do the boundary survey first, then we'll move to the topo."
                  style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
                    fontFamily: 'inherit', resize: 'vertical',
                  }}
                />
              </div>
            )}

            {/* Typed e-signature */}
            {!showDeclineForm && (
              <div style={{ marginBottom: '20px', textAlign: 'left', maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Sign here &mdash; type your full name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Your full legal name"
                  autoComplete="name"
                  style={{
                    width: '100%', padding: '12px', border: '1px solid #d1d5db',
                    borderRadius: '8px', fontSize: '20px', boxSizing: 'border-box',
                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
                    color: '#166534',
                  }}
                />
                <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#6b7280', lineHeight: '1.5' }}>
                  By typing your name and clicking Approve, you agree to the selected items and the total
                  shown above. Your name, the date and time, and your IP address are recorded with this
                  approval. This electronic signature is legally binding under the federal ESIGN Act and
                  Georgia UETA.
                </p>
              </div>
            )}

            {!showDeclineForm ? (
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={handleApprove}
                  disabled={submitting}
                  style={{
                    padding: '14px 48px',
                    backgroundColor: '#16a34a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: submitting ? 'wait' : 'pointer',
                    opacity: submitting ? 0.7 : 1
                  }}
                >
                  {submitting ? 'Processing...' : 'Approve Quote'}
                </button>
                <button
                  onClick={() => setShowDeclineForm(true)}
                  disabled={submitting}
                  style={{
                    padding: '14px 48px',
                    backgroundColor: 'white',
                    color: '#dc2626',
                    border: '2px solid #dc2626',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Decline
                </button>
              </div>
            ) : (
              <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                <textarea
                  placeholder="Please let us know why (optional)..."
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical',
                    marginBottom: '16px',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    onClick={handleDecline}
                    disabled={submitting}
                    style={{
                      padding: '12px 32px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: submitting ? 'wait' : 'pointer',
                      opacity: submitting ? 0.7 : 1
                    }}
                  >
                    {submitting ? 'Processing...' : 'Confirm Decline'}
                  </button>
                  <button
                    onClick={() => setShowDeclineForm(false)}
                    style={{
                      padding: '12px 32px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: '12px' }}>
          <p>Powered by SurveyPro</p>
        </div>
      </div>
    </div>
  );
}

export default PublicQuoteApproval;
